/*!
 * CASEVA GLASS THEME — ANIMATIONS ENGINE
 * Handles: particle canvas, tilt cards, magnetic buttons,
 *          cursor glow, scroll reveals, counter animation
 */

(function () {
  'use strict';

  /* ============================================================
     DEVICE DETECTION — skip heavy effects on touch/mobile
     ============================================================ */
  const isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
  const isMobile = window.matchMedia('(max-width: 768px)').matches;

  /* ============================================================
     1. CURSOR GLOW
     ============================================================ */
  function initCursorGlow() {
    if (isTouchDevice || isMobile) return; // skip on touch/mobile — no mouse cursor
    const glow = document.createElement('div');
    glow.style.cssText = `
      position: fixed;
      width: 300px; height: 300px;
      border-radius: 50%;
      background: radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%);
      pointer-events: none;
      z-index: 9999;
      transform: translate(-50%, -50%);
      transition: opacity 0.3s ease;
      mix-blend-mode: screen;
    `;
    document.body.appendChild(glow);

    let mx = 0, my = 0, cx = 0, cy = 0;

    document.addEventListener('mousemove', e => {
      mx = e.clientX; my = e.clientY;
      glow.style.opacity = '1';
    });

    document.addEventListener('mouseleave', () => { glow.style.opacity = '0'; });

    function animateCursor() {
      cx += (mx - cx) * 0.1;
      cy += (my - cy) * 0.1;
      glow.style.left = cx + 'px';
      glow.style.top  = cy + 'px';
      requestAnimationFrame(animateCursor);
    }
    animateCursor();
  }

  /* ============================================================
     2. MAGNETIC BUTTONS
     ============================================================ */
  function initMagneticButtons() {
    if (isTouchDevice || isMobile) return; // magnetic effect only for mouse
    const btns = document.querySelectorAll('.btn, .glass-hero__btn, .site-nav__link');
    btns.forEach(btn => {
      btn.addEventListener('mousemove', e => {
        const rect = btn.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = (e.clientX - cx) * 0.25;
        const dy = (e.clientY - cy) * 0.25;
        btn.style.transform = `translate(${dx}px, ${dy}px)`;
      });
      btn.addEventListener('mouseleave', () => {
        btn.style.transform = '';
        btn.style.transition = 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)';
        setTimeout(() => { btn.style.transition = ''; }, 400);
      });
    });
  }

  /* ============================================================
     3. GLASS TILT CARDS
     ============================================================ */
  function initTiltCards() {
    if (isTouchDevice || isMobile) return; // tilt is mouse-only, skip on touch
    const cards = document.querySelectorAll('.grid-product__content, .collection-callout, .testimonials__slide');
    cards.forEach(card => {
      card.addEventListener('mousemove', e => {
        const rect = card.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const rotY = ((e.clientX - cx) / (rect.width / 2)) * 6;
        const rotX = -((e.clientY - cy) / (rect.height / 2)) * 6;
        card.style.transform = `perspective(1000px) rotateX(${rotX}deg) rotateY(${rotY}deg) translateY(-4px) scale(1.02)`;
        card.style.transition = 'transform 0.1s ease';

        // Specular highlight
        const nx = (e.clientX - rect.left) / rect.width * 100;
        const ny = (e.clientY - rect.top) / rect.height * 100;
        card.style.background = `radial-gradient(circle at ${nx}% ${ny}%, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.04) 40%, transparent 70%)`;
      });

      card.addEventListener('mouseleave', () => {
        card.style.transform = '';
        card.style.background = '';
        card.style.transition = 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1), background 0.5s ease';
      });
    });
  }

  /* ============================================================
     4. SCROLL REVEAL ANIMATION
     ============================================================ */
  function initScrollReveal() {
    const targets = document.querySelectorAll(
      '.index-section, .grid-product, .section-header, .collection-callout, .testimonials__slide, .rich-text__text'
    );

    targets.forEach((el, i) => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(30px)';
      el.style.transition = `opacity 0.7s ease ${(i % 4) * 0.1}s, transform 0.7s cubic-bezier(0.4, 0, 0.2, 1) ${(i % 4) * 0.1}s`;
    });

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });

    targets.forEach(el => observer.observe(el));
  }

  /* ============================================================
     5. PARTICLE CANVAS (hero background)
     ============================================================ */
  function initParticleCanvas() {
    if (isMobile) return; // Skip particles on mobile for performance
    const hero = document.querySelector('.glass-hero, .video-parent-section.hero');
    if (!hero) return;

    const canvas = document.createElement('canvas');
    canvas.style.cssText = 'position:absolute;top:0;left:0;z-index:5;pointer-events:none;opacity:0.4;';
    hero.style.position = 'relative';
    hero.appendChild(canvas);

    const ctx = canvas.getContext('2d');
    let W, H, particles = [];

    function resize() {
      W = canvas.width = hero.offsetWidth;
      H = canvas.height = hero.offsetHeight;
    }

    class Particle {
      constructor() { this.reset(); }
      reset() {
        this.x = Math.random() * W;
        this.y = Math.random() * H;
        this.size = Math.random() * 1.5 + 0.3;
        this.speedX = (Math.random() - 0.5) * 0.3;
        this.speedY = -(Math.random() * 0.4 + 0.1);
        this.alpha = Math.random() * 0.6 + 0.2;
        this.life = 0;
        this.maxLife = Math.random() * 200 + 100;
      }
      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        this.life++;
        if (this.life > this.maxLife || this.y < -10) this.reset();
      }
      draw() {
        const progress = this.life / this.maxLife;
        const fade = progress < 0.1 ? progress / 0.1 : progress > 0.8 ? 1 - (progress - 0.8) / 0.2 : 1;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${this.alpha * fade})`;
        ctx.fill();
      }
    }

    resize();
    for (let i = 0; i < 80; i++) particles.push(new Particle());
    window.addEventListener('resize', resize);

    function animate() {
      ctx.clearRect(0, 0, W, H);
      particles.forEach(p => { p.update(); p.draw(); });
      requestAnimationFrame(animate);
    }
    animate();
  }

  /* ============================================================
     6. TEXT SCRAMBLE ON HERO TITLE — DISABLED
     (Was scrambling title with random chars/numbers on load,
      making "Made for the Best" appear broken)
     ============================================================ */
  function initTextScramble() {
    // Scramble effect disabled — it displayed random characters/numbers
    // on the hero title which confused users. Title now shows cleanly.
    return;
  }

  /* ============================================================
     7. COUNTER ANIMATION (for stat badges)
     ============================================================ */
  function initCounters() {
    const counters = document.querySelectorAll('.glass-hero__stat-num, [data-count]');
    counters.forEach(counter => {
      const target = parseInt(counter.getAttribute('data-count') || counter.innerText, 10);
      if (isNaN(target)) return;
      let current = 0;
      const step = target / 60;
      const timer = setInterval(() => {
        current += step;
        if (current >= target) { counter.innerText = target + (counter.dataset.suffix || ''); clearInterval(timer); }
        else counter.innerText = Math.floor(current) + (counter.dataset.suffix || '');
      }, 16);
    });
  }

  /* ============================================================
     8. PARALLAX SCROLLING
     ============================================================ */
  function initParallax() {
    const hero = document.querySelector('.glass-hero__video, .hero__media video');
    if (!hero) return;
    window.addEventListener('scroll', () => {
      const y = window.scrollY;
      hero.style.transform = `translateY(${y * 0.35}px)`;
    }, { passive: true });
  }

  /* ============================================================
     9. HEADER SCROLL EFFECT
     ============================================================ */
  function initHeaderScroll() {
    const header = document.querySelector('#SiteHeader, .site-header');
    if (!header) return;
    let lastY = 0;
    window.addEventListener('scroll', () => {
      const y = window.scrollY;
      // White theme: use translucent white, not dark
      if (y > 80) {
        header.style.background = 'rgba(255,255,255,0.95)';
        header.style.backdropFilter = 'blur(24px)';
        header.style.webkitBackdropFilter = 'blur(24px)';
        header.style.borderBottomColor = 'rgba(0,0,0,0.12)';
      } else {
        header.style.background = 'rgba(255,255,255,0.75)';
        header.style.backdropFilter = 'blur(18px)';
        header.style.webkitBackdropFilter = 'blur(18px)';
        header.style.borderBottomColor = 'rgba(0,0,0,0.08)';
      }
      // Hide on scroll down (only on desktop — mobile needs stable header)
      if (!isMobile) {
        if (y > lastY + 5 && y > 200) {
          header.style.transform = 'translateY(-100%)';
        } else if (y < lastY - 5) {
          header.style.transform = 'translateY(0)';
        }
      } else {
        header.style.transform = 'translateY(0)'; // Always visible on mobile
      }
      lastY = y;
    }, { passive: true });
  }

  /* ============================================================
     10. VIDEO PRELOAD & FADE
     ============================================================ */
  function initVideoFade() {
    const videos = document.querySelectorAll('.glass-hero__video, .hero__media video');
    videos.forEach(v => {
      v.addEventListener('canplaythrough', () => v.classList.add('loaded'));
      if (v.readyState >= 3) v.classList.add('loaded');
    });
  }

  /* ============================================================
     INIT ALL
     ============================================================ */
  function init() {
    initCursorGlow();
    initScrollReveal();
    initParticleCanvas();
    initTextScramble();
    initParallax();
    initHeaderScroll();
    initVideoFade();

    // Defer heavier effects
    setTimeout(() => {
      initMagneticButtons();
      initTiltCards();
      initCounters();
    }, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
